/**
 * GP Ads AI - Budget Adjustment Hook
 * 
 * PRD-3604: E004-S004 - Adjust Campaign Budgets
 */

import { useState, useCallback, useMemo } from 'react';
import { budgetApi, BudgetAdjustmentRequest, BudgetAdjustmentResponse } from '../lib/budgetApi';

interface UseBudgetAdjustmentOptions {
  onSuccess?: (result: BudgetAdjustmentResponse) => void;
  onError?: (error: Error) => void;
}

interface UseBudgetAdjustmentReturn {
  // State
  newBudget: number;
  setNewBudget: (value: number) => void;
  isLoading: boolean;
  error: Error | null;
  result: BudgetAdjustmentResponse | null;
  
  // Computed values
  changeAmount: number;
  changePercent: number;
  requiresApproval: boolean;
  approvalLevel: 'auto_approve' | 'manager_approval' | 'director_approval';
  isValid: boolean;
  validationError: string | null;
  
  // Actions
  adjustBudget: (request: BudgetAdjustmentRequest) => Promise<BudgetAdjustmentResponse | null>;
  reset: () => void;
  
  // Quick adjustments
  quickAdjustments: Array<{ label: string; value: number }>;
  applyQuickAdjustment: (value: number) => void;
}

// Guardrail constants (match backend)
const MIN_BUDGET = 10;
const MAX_INCREASE_RATIO = 5;

export function useBudgetAdjustment(
  currentBudget: number,
  options: UseBudgetAdjustmentOptions = {}
): UseBudgetAdjustmentReturn {
  const { onSuccess, onError } = options;

  // State
  const [newBudget, setNewBudget] = useState(currentBudget);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [result, setResult] = useState<BudgetAdjustmentResponse | null>(null);

  // Computed values
  const changeAmount = useMemo(
    () => newBudget - currentBudget,
    [newBudget, currentBudget]
  );

  const changePercent = useMemo(
    () => budgetApi.calculateChangePercent(currentBudget, newBudget),
    [currentBudget, newBudget]
  );

  const requiresApproval = useMemo(
    () => budgetApi.requiresApproval(currentBudget, newBudget),
    [currentBudget, newBudget]
  );

  const approvalLevel = useMemo(
    () => budgetApi.getApprovalLevel(currentBudget, newBudget),
    [currentBudget, newBudget]
  );

  // Validation
  const { isValid, validationError } = useMemo(() => {
    // Check minimum budget
    if (newBudget < MIN_BUDGET) {
      return { 
        isValid: false, 
        validationError: `Budget cannot be below $${MIN_BUDGET}` 
      };
    }

    // Check zero or negative
    if (newBudget <= 0) {
      return { 
        isValid: false, 
        validationError: 'Budget must be greater than $0' 
      };
    }

    // Check maximum increase
    if (currentBudget > 0 && newBudget > currentBudget * MAX_INCREASE_RATIO) {
      return { 
        isValid: false, 
        validationError: `Cannot increase budget more than ${MAX_INCREASE_RATIO}x` 
      };
    }

    // Check no change
    if (newBudget === currentBudget) {
      return { 
        isValid: false, 
        validationError: null // Not an error, just disabled
      };
    }

    return { isValid: true, validationError: null };
  }, [newBudget, currentBudget]);

  // Quick adjustments
  const quickAdjustments = useMemo(
    () => budgetApi.getQuickAdjustments(currentBudget),
    [currentBudget]
  );

  // Actions
  const adjustBudget = useCallback(
    async (request: BudgetAdjustmentRequest): Promise<BudgetAdjustmentResponse | null> => {
      if (!isValid) {
        const err = new Error(validationError || 'Invalid budget change');
        setError(err);
        onError?.(err);
        return null;
      }

      setIsLoading(true);
      setError(null);
      setResult(null);

      try {
        const response = await budgetApi.adjustBudget(request);
        setResult(response);
        onSuccess?.(response);
        return response;
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to adjust budget');
        setError(error);
        onError?.(error);
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [isValid, validationError, onSuccess, onError]
  );

  const reset = useCallback(() => {
    setNewBudget(currentBudget);
    setError(null);
    setResult(null);
  }, [currentBudget]);

  const applyQuickAdjustment = useCallback((value: number) => {
    setNewBudget(Math.round(value * 100) / 100); // Round to cents
  }, []);

  return {
    // State
    newBudget,
    setNewBudget,
    isLoading,
    error,
    result,
    
    // Computed
    changeAmount,
    changePercent,
    requiresApproval,
    approvalLevel,
    isValid,
    validationError,
    
    // Actions
    adjustBudget,
    reset,
    quickAdjustments,
    applyQuickAdjustment
  };
}

export default useBudgetAdjustment;
